import { normaliseDecimal } from '@/features/finance/lines'
import { fieldErrors } from '@/lib/problems'
import type { Invoice, InvoiceLine, InvoiceShipment, InvoiceSummary } from '@/types/domain'

/* The client invoice, as the server serves it and as the editor writes it back
 * (Story 5.4, FR-61).
 *
 * The four READ shapes are `types/domain`'s and are only re-exported here, so
 * the imports in this feature keep reading as they did. They used to be declared
 * twice, which is how they came to disagree: this file still said
 * `status: 'draft'` was the whole union and knew nothing of `trancheId`,
 * `settlement`, `allocatedUsd` or `issuedAt` — fields the server had begun
 * sending — while `types/domain` had them and nothing imported it. Two exported
 * types with one name and different shapes is worse than either of them,
 * because whichever one a file imports is invisible at the call site.
 *
 * What stays here is what really is this feature's: the currency list its picker
 * offers, the WRITE payloads, and the editor's own arithmetic. Money crosses as
 * decimal STRINGS in both directions (AD-9) — nothing here widens an amount to
 * `number` except the display arithmetic at the bottom, which is labelled as
 * such.
 *
 * The one thing worth reading twice is `rateCeilingBreach`. AD-10 refuses a
 * line at the point of ADDING it, and the server deliberately sends no figures
 * inside that refusal: the client already holds `approvedRateUsd` and
 * `billedUsd` on the invoice it is editing, and a sentence composed here is a
 * sentence in the reader's own language rather than English on a Russian
 * screen (AD-27).
 */

/** The three currencies an invoice may print in (AC-6, FR-61). NOT the payment
 *  currency list next door: a payment lands in whatever the bank sent, while a
 *  document is printed in one of three the trade actually invoices in. */
export const INVOICE_CURRENCIES = ['USD', 'CNY', 'EUR'] as const

export type InvoiceCurrency = (typeof INVOICE_CURRENCIES)[number]

/** What the desk calls each code on screen.
 *
 *  The wire value is the ISO code, because that is what 0008's `currency_code`
 *  domain stores and what a rate lookup keys on — `finance.DocumentCurrencies`
 *  refuses "RMB" outright, and the story's own "USD, RMB and EUR" is the desk's
 *  vocabulary, not the column's. So the spelling differs in exactly one place:
 *  the label a person reads. Anywhere else would be two names for one currency
 *  inside the application, which is how a rate lookup silently misses. */
export function currencyLabel(currency: InvoiceCurrency): string {
  return currency === 'CNY' ? 'RMB' : currency
}

/** The four READ shapes, re-exported from `types/domain` and declared there.
 *
 *  `types/domain` is what the wire contract is written against — it carries the
 *  issue facts (`status`, `issuedAt`, `trancheId`), the derived settlement and
 *  the shipment block's nullable dates — and a second declaration beside it was
 *  a second answer to the same question that had already gone stale. */
export type { Invoice, InvoiceLine, InvoiceShipment, InvoiceSummary }

/** What POST /cases/:id/invoices takes. The header is NOT here: client, codes
 *  and both reference numbers are copied from the case server-side, and the
 *  FX rate is resolved there too (AD-20). */
export interface NewInvoicePayload {
  currency: InvoiceCurrency
  invoiceDate: string
  economicDate: string
  customerReference: string
  /** The instalment this invoice bills, or `''` for one outside the series.
   *
   *  `''` IS THE ORDINARY VALUE, not a blank waiting to be filled: a re-billing,
   *  a correction, and a case with no payment series at all are all invoices
   *  that displace nothing. The server has read the field that way since the
   *  endpoint was written (`invoiceBody.tranche`, `finance/invoice_handlers.go`)
   *  — absent and blank are the same answer there, and only a value that will
   *  not parse as a uuid is a problem — so a payload that omits it is legal and
   *  says "outside the series".
   *
   *  It was missing from this type, and the consequence was not cosmetic: every
   *  invoice this client raised was raised outside the series, so an issued
   *  demand displaced no tranche. The tranche went on being an allocation target
   *  beside the invoice that was billing it, and the case reported the same
   *  money owed twice until somebody reconciled by hand. */
  trancheId?: string
  vatApplies: boolean
  vatRate: string
}

/** What PUT /cases/:id/invoices/:invoiceId takes.
 *
 *  Neither the currency nor the economic date is here, and the absence is the
 *  AD-9 freeze itself: a caller with the wrong currency raises a new invoice,
 *  which is honest paper rather than a figure rewritten under a number. */
export interface InvoiceHeaderPayload {
  invoiceDate: string
  customerReference: string
  /** The instalment link again, and it has to be sent on EVERY header save.
   *
   *  The PUT is a whole-header replace, and `invoiceBody.change()` reads an
   *  absent or blank `trancheId` as an instruction to UNLINK — its own comment
   *  says so, and `TestOmittingTheTrancheIdOnTheHeaderPutUnlinksTheInstalment`
   *  pins it. So a form that leaves the field out does not leave the link alone:
   *  it drops it, and the tranche that had stopped being owed starts being owed
   *  again beside the invoice that was billing it.
   *
   *  Which is why this sits on the EDIT payload even though the currency and the
   *  economic date deliberately do not. Those two are frozen by AD-9 and their
   *  absence is the freeze; this one is a link a draft may legitimately move,
   *  and its absence would be a silent write rather than a refused one. */
  trancheId?: string
  vatApplies: boolean
  vatRate: string
}

export interface InvoiceLinePayload {
  description: string
  quantity: string
  unitPrice: string
  amount: string
}

/** Today as `YYYY-MM-DD`, which is how every date on this surface is spelled.
 *
 *  LOCAL, not UTC. `toISOString` would answer yesterday for everyone in this
 *  company between midnight and 05:00 in Ashgabat or 08:00 in Yiwu — and the
 *  date it fills in is the ECONOMIC date, which is the day the FX rate books
 *  against (FR-55). A rate a day early is a different figure on the invoice,
 *  arrived at by nobody's decision. */
export function today(): string {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}

/** Accepts `1234.56`, `1 234,56`; rejects text and blanks. Six places, because
 *  a quantity is not money — detention is billed in part-days. */
export function isAmountLike(raw: string): boolean {
  const normalised = normaliseDecimal(raw)
  return normalised !== '' && /^\d+(\.\d{1,6})?$/.test(normalised)
}

/** The same, for a figure that is BILLED: two decimal places, no more.
 *
 *  The amount column is `numeric(12,2)` and the AD-9 booking is what rounds, so
 *  a third decimal place is rounded twice and the two answers need not agree —
 *  `3.607` on a CNY invoice at 0.14 books 0.50 USD from the figure sent and
 *  0.51 from the figure stored, which the row's consistency CHECK rejects as a
 *  500 the server cannot map to a field. Where the arithmetic happens not to
 *  straddle a cent it is quieter and no better: the invoice prints an amount
 *  nobody typed. The server refuses it (`InvoiceLineInput.Validate`); this
 *  refuses it before the round trip, beside the box.
 *
 *  Trailing zeros are not a decimal place: `3.500` is 3.50 to the column and to
 *  the reader, and the server accepts it, so this must too — a stricter client
 *  would refuse a figure with nothing wrong with it and leave the typist
 *  hunting for the fault. */
export function isBillableAmount(raw: string): boolean {
  const normalised = normaliseDecimal(raw)
  if (normalised === '' || !/^\d+(\.\d+)?$/.test(normalised)) return false
  const [, decimals = ''] = normalised.split('.')
  return decimals.replace(/0+$/, '').length <= 2
}

/** A charge is submittable when it names something and carries a positive
 *  figure. Zero is refused with the negatives, as the server refuses it: a
 *  charge at 0.00 reads on a client's invoice as a fee waived rather than as a
 *  figure nobody typed. */
export function isChargeComplete(draft: InvoiceLinePayload): boolean {
  return (
    draft.description.trim() !== '' &&
    isBillableAmount(draft.amount) &&
    Number(normaliseDecimal(draft.amount)) > 0 &&
    (draft.quantity === '' || isAmountLike(draft.quantity)) &&
    (draft.unitPrice === '' || isAmountLike(draft.unitPrice))
  )
}

export function emptyCharge(): InvoiceLinePayload {
  return { description: '', quantity: '', unitPrice: '', amount: '' }
}

/** Quantity × unit price as a two-place decimal string, exactly.
 *
 *  The editor shows this beside the amount box as a CHECK on the figure being
 *  typed, so its whole job is to be compared against a number a person read off
 *  a rate sheet. `Math.round(q * p * 100) / 100` is a cent out on the half-cent
 *  products — 0.145 × 100 is 14.499999999999998 in floats and rounds down — and
 *  a check figure that disagrees with the correct amount by a cent is worse
 *  than none: it sends the typist to correct a figure that was right.
 *
 *  So the same exact path the AD-10 message uses: scaled integers, one rounding
 *  step at the end, half away from zero — which is what the server's decimal
 *  rounds. `undefined` when either side is not a number, which is the caller's
 *  cue to show the plain hint instead. */
export function productOf(quantity: string, unitPrice: string): string | undefined {
  const q = parseScaled(quantity)
  const p = parseScaled(unitPrice)
  if (q === undefined || p === undefined) return undefined

  const cents = rescale(q.units * p.units, q.scale + p.scale, 2)
  const negative = cents < 0n
  const magnitude = negative ? -cents : cents
  const whole = magnitude / 100n
  const fraction = String(magnitude % 100n).padStart(2, '0')
  return `${negative ? '-' : ''}${whole}.${fraction}`
}

/** The decimal places a figure was written with, so it can be RENDERED at the
 *  precision it was stored with rather than at a fixed two.
 *
 *  Trailing zeros do not count: `1.500` is one place to a reader, and padding
 *  it back out to three would be this function inventing precision of its own.
 *  Capped at six, which is what `isAmountLike` accepts. */
export function decimalPlaces(raw: string): number {
  const [, fraction = ''] = normaliseDecimal(raw).split('.')
  return Math.min(fraction.replace(/0+$/, '').length, 6)
}

/** The AD-10 refusal, recognised by its field code rather than by its message.
 *
 *  The server answers 422 with `{amount: ["exceeds-approved-rate"]}` and no
 *  numbers (finance/handlers.go). Matching on the CODE is what keeps this
 *  working when somebody rewords the server's English `title`. */
export function isRateCeilingRefusal(error: unknown): boolean {
  return (fieldErrors(error).amount ?? []).includes('exceeds-approved-rate')
}

/** The three figures the AD-10 message names, in USD at booked rates.
 *
 *  DISPLAY arithmetic, and the only arithmetic in this file — the same licence
 *  `lines.ts` takes for its running total, and taken for the same reason: the
 *  numbers are read, never sent. The server has already made the decision this
 *  sentence explains, and it made it on decimals.
 *
 *  `undefined` when the case has no agreed rate, because then there is no
 *  ceiling and no excess to name; the caller falls back to a plain refusal. */
export function rateCeilingBreach(
  invoice: InvoiceSummary,
  /** The charge that was refused, in the INVOICE's currency. */
  candidateAmount: string,
  /** The charge it would have replaced, when the refusal came from an edit
   *  rather than an add. An edit that lowers a line still has to clear the
   *  ceiling, but it clears it without its own old figure counted twice. */
  replacingAmount = '0',
): { approvedUsd: number; billedUsd: number; excessUsd: number } | undefined {
  if (invoice.approvedRateUsd === null) return undefined

  const approved = toCents(invoice.approvedRateUsd)
  const billed = toCents(invoice.billedUsd)
  const rate = parseScaled(invoice.fxRate)
  if (approved === undefined || billed === undefined) return undefined
  if (rate === undefined || rate.units <= 0n) return undefined

  // Rounded at line-total level, ONCE, exactly where the server rounds it
  // (finance.Book) — and in integers, which is the part that has to match. The
  // server multiplies decimals; a float multiply here disagrees with it on the
  // half-cent products, so a refusal judged at 0.01 over could be explained to
  // the user as 0.00 over, or the other way about. The only figure the message
  // names would then be the one figure that was not the reason.
  const converted = (amount: string) => multiplyToCents(amount, rate)
  const candidate = converted(candidateAmount)
  const replacing = converted(replacingAmount)
  if (candidate === undefined || replacing === undefined) return undefined

  const excess = billed + candidate - replacing - approved

  // A refusal whose arithmetic does not reproduce an excess is a refusal this
  // client cannot explain — better a plain message than a confident "0.00 over".
  if (excess <= 0n) return undefined

  return {
    approvedUsd: Number(approved) / 100,
    billedUsd: Number(billed) / 100,
    excessUsd: Number(excess) / 100,
  }
}

/** The four AD-10 figures a desk reads BEFORE it is refused anything.
 *
 *  `rateCeilingBreach` above explains a refusal that has already happened.
 *  This is the same ceiling shown standing: what was agreed, what earlier
 *  invoices took of it, what this one takes, and what is left for the next.
 *  Both read the figures the server already booked and neither sends anything
 *  back — the same display licence, taken for the same reason.
 *
 *  `billedUsd` is the case's whole billed total and INCLUDES this invoice's own
 *  lines, which is what `rateCeilingBreach` relies on when it adds a candidate
 *  charge to it. So "earlier" is that total minus this invoice, not the total
 *  itself.
 *
 *  `undefined` when the case carries no agreed rate: there is then no ceiling
 *  to draw, and drawing one at zero would report every invoice as over it. */
export interface RateCeiling {
  approvedUsd: number
  /** What OTHER invoices on this case have already billed. */
  earlierUsd: number
  thisInvoiceUsd: number
  /** Approved minus everything billed, this invoice included. Negative when the
   *  case is already past its ceiling, which is a state the screen states
   *  rather than clamps — a headroom floored at zero would hide the overrun. */
  remainingUsd: number
}

export function rateCeiling(invoice: InvoiceSummary): RateCeiling | undefined {
  if (invoice.approvedRateUsd === null) return undefined

  const approved = toCents(invoice.approvedRateUsd)
  const billed = toCents(invoice.billedUsd)
  const own = toCents(invoice.totalUsd)
  if (approved === undefined || billed === undefined || own === undefined) {
    return undefined
  }

  return {
    approvedUsd: Number(approved) / 100,
    earlierUsd: Number(billed - own) / 100,
    thisInvoiceUsd: Number(own) / 100,
    remainingUsd: Number(approved - billed) / 100,
  }
}

/** Whether two decimal strings name the same money.
 *
 *  A COMPARISON, not arithmetic: `'5975'`, `'5975.0'` and `'5 975,00'` are one
 *  figure, and `===` on the raw strings says they are three. Through the same
 *  exact cent path everything else in this file uses, so the answer cannot
 *  disagree with the totals rendered beside it. False when either side is not a
 *  number at all — an unanswerable comparison is not a match. */
export function sameAmount(left: string, right: string): boolean {
  const a = toCents(left)
  const b = toCents(right)
  return a !== undefined && b !== undefined && a === b
}

/** A decimal string as an exact integer and the scale it was written at.
 *
 *  `1.005` becomes `{units: 1005n, scale: 3}`, which is the same figure the
 *  server's decimal type holds — and unlike `Number('1.005')` it is not
 *  0.100499999999999989 to begin with. */
function parseScaled(value: string): { units: bigint; scale: number } | undefined {
  const text = normaliseDecimal(value).trim()
  if (!/^-?\d+(\.\d+)?$/.test(text)) return undefined

  const [whole = '', fraction = ''] = text.split('.')
  const negative = whole.startsWith('-')
  const digits = whole.replace('-', '') + fraction
  const units = BigInt(digits === '' ? '0' : digits)
  return { units: negative ? -units : units, scale: fraction.length }
}

/** A decimal string as whole cents, rounding half away from zero. */
function toCents(value: string): bigint | undefined {
  const parsed = parseScaled(value)
  return parsed === undefined ? undefined : rescale(parsed.units, parsed.scale, 2)
}

/** amount × rate, as whole cents, with ONE rounding step at the end.
 *
 *  An empty amount converts to zero rather than to a failure: the caller asks
 *  about a line it may be adding rather than replacing. */
function multiplyToCents(
  amount: string,
  rate: { units: bigint; scale: number },
): bigint | undefined {
  if (normaliseDecimal(amount).trim() === '') return 0n
  const parsed = parseScaled(amount)
  if (parsed === undefined) return undefined
  return rescale(parsed.units * rate.units, parsed.scale + rate.scale, 2)
}

/** Restates a scaled integer at `places` decimals, rounding half away from
 *  zero — which is what shopspring's `Round` does on the server side. */
function rescale(units: bigint, from: number, places: number): bigint {
  if (from <= places) return units * 10n ** BigInt(places - from)

  const divisor = 10n ** BigInt(from - places)
  const negative = units < 0n
  const magnitude = negative ? -units : units
  const quotient = magnitude / divisor
  const rounded = magnitude % divisor * 2n >= divisor ? quotient + 1n : quotient
  return negative ? -rounded : rounded
}
